import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { updateSiteContent } from "../../home/shared/services/siteContentService";
import {
  normalizeSiteContent,
  SiteContent,
} from "../../home/shared/types/siteContent";

type EditableContentSection =
  | "highlights"
  | "events"
  | "products"
  | "instagram";

type UseAdminContentEditorParams = {
  content: SiteContent;
  isAdmin: boolean;
  setContent: Dispatch<SetStateAction<SiteContent>>;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  token?: string | null;
};

function randomHex(bytes = 6) {
  const buffer = new Uint8Array(bytes);
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    crypto.getRandomValues(buffer);
  } else {
    for (let index = 0; index < buffer.length; index += 1) {
      buffer[index] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(buffer)
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

function generateEventId(existing: Set<string>) {
  let id = "";
  do {
    id = `evt_${randomHex(6)}`;
  } while (existing.has(id));
  return id;
}

export function useAdminContentEditor({
  content,
  isAdmin,
  setContent,
  showErrorToast,
  showSuccessToast,
  token,
}: UseAdminContentEditorParams) {
  const t = siteTexts.admin;
  const [contentDraft, setContentDraft] = useState<SiteContent>(content);
  const [savingContent, setSavingContent] = useState(false);
  const autoSaveTimeoutRef = useRef<number | null>(null);
  const skipNextAutoSaveRef = useRef(true);

  useEffect(() => {
    setContentDraft(content);
    skipNextAutoSaveRef.current = true;
  }, [content]);

  const persistContent = useCallback(
    async (nextContent: SiteContent, successMessage?: string) => {
      if (!token) return;
      try {
        setSavingContent(true);
        const persisted = await updateSiteContent(token, nextContent);
        const normalized = normalizeSiteContent(
          persisted.content || nextContent,
        );
        setContent(normalized);
        setContentDraft(normalized);
        if (successMessage) {
          showSuccessToast(successMessage);
        }
      } catch (_err) {
        showErrorToast(siteTexts.admin.toasts.saveContentError);
      } finally {
        setSavingContent(false);
      }
    },
    [token, setContent, showSuccessToast, showErrorToast],
  );

  const saveContent = async () => {
    await persistContent(contentDraft, t.toasts.saveContentSuccess);
  };

  useEffect(() => {
    if (!isAdmin || !token) return;
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }

    const draftJson = JSON.stringify(contentDraft);
    const persistedJson = JSON.stringify(content);
    if (draftJson === persistedJson) {
      return;
    }

    if (autoSaveTimeoutRef.current !== null) {
      window.clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = window.setTimeout(() => {
      void persistContent(contentDraft);
    }, 900);

    return () => {
      if (autoSaveTimeoutRef.current !== null) {
        window.clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    };
  }, [contentDraft, content, isAdmin, token, persistContent]);

  const updateHero = (field: string, value: string) => {
    setContentDraft((prev) => ({
      ...prev,
      hero: {
        ...prev.hero,
        [field]: value,
      },
    }));
  };

  const updateAbout = (field: string, value: string | string[]) => {
    setContentDraft((prev) => ({
      ...prev,
      about: {
        ...prev.about,
        [field]: value,
      },
    }));
  };

  const updateListItem = (
    section: EditableContentSection,
    index: number,
    field: string,
    value: string,
  ) => {
    setContentDraft((prev) => {
      const list = [...(prev as any)[section]];
      list[index] = {
        ...list[index],
        [field]: value,
      };
      return {
        ...prev,
        [section]: list,
      };
    });
  };

  const addListItem = (section: EditableContentSection) => {
    const defaults: Record<string, any> = {
      highlights: { title: "", description: "", imageUrl: "" },
      events: {
        id: "",
        title: "",
        date: "",
        address: "",
        description: "",
        details: "",
        imageUrl: "",
      },
      products: { title: "", price: "", description: "", imageUrl: "" },
      instagram: {
        imageUrl: "",
        link: "https://instagram.com/todosnopadel",
      },
    };

    setContentDraft((prev) => {
      if (section !== "events") {
        return {
          ...prev,
          [section]: [...(prev as any)[section], defaults[section]],
        };
      }

      const usedIds = new Set(
        (prev.events || [])
          .map((item) => String((item as any)?.id || "").trim())
          .filter(Boolean),
      );
      const nextEvent = { ...defaults.events, id: generateEventId(usedIds) };
      return {
        ...prev,
        events: [...(prev.events || []), nextEvent],
      };
    });
  };

  const removeListItem = (section: EditableContentSection, index: number) => {
    setContentDraft((prev) => ({
      ...prev,
      [section]: (prev as any)[section].filter(
        (_item: any, idx: number) => idx !== index,
      ),
    }));
  };

  const removeHeroImage = async () => {
    const next = {
      ...contentDraft,
      hero: {
        ...contentDraft.hero,
        imageUrl: "",
      },
    };
    setContentDraft(next);
    await persistContent(next, t.toasts.imageRemovedSuccess);
  };

  const removeListImage = async (
    section: EditableContentSection,
    index: number,
  ) => {
    const next = {
      ...contentDraft,
      [section]: (contentDraft as any)[section].map((item: any, idx: number) =>
        idx === index ? { ...item, imageUrl: "" } : item,
      ),
    } as SiteContent;
    setContentDraft(next);
    await persistContent(next, t.toasts.imageRemovedSuccess);
  };

  return {
    addListItem,
    contentDraft,
    removeHeroImage,
    removeListImage,
    removeListItem,
    saveContent,
    savingContent,
    updateAbout,
    updateHero,
    updateListItem,
  };
}
